import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X as XIcon, CaretRight } from "@phosphor-icons/react";

import { SEO } from "@/components/SEO";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { SectionHeading, Eyebrow, BookDemoButton, LinkButton } from "@/components/marketing/Primitives";
import { OpsDashboard, OpsCalendar, EngineerApp, BrowserFrame } from "@/components/marketing/ProductMockups";
import { HardHat, Wrench as WrenchIcon, UsersThree, Quotes, Gift, Check as CheckMini } from "@phosphor-icons/react";
import { useDemo } from "@/context/DemoContext";
import {
  platformModules, keyFeatures, industries, comparison, pricingTiers, leanStats,
} from "@/lib/content";

const ELEC_IMG = "https://images.pexels.com/photos/33694019/pexels-photo-33694019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const heroLines = ["Run Your Entire", "Field Service Business", "From One Platform"];

function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 pb-16 sm:pt-36 lg:pt-40">
      <div className="pointer-events-none absolute inset-0 radial-fade" />
      <div className="pointer-events-none absolute inset-0 dotgrid opacity-40" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ opacity }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Eyebrow>The operating platform for field service</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-[4.2rem]">
            {heroLines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? <span className="text-primary">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
          >
            FlowCore combines CRM, scheduling, engineers, servicing, asset management, stock control,
            customer portals and AI into one powerful cloud platform.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <BookDemoButton testid="hero-book-demo" />
            <LinkButton to="/features" testid="hero-view-features">
              View Features <ArrowRight size={16} weight="bold" />
            </LinkButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-8 flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Check size={16} weight="bold" className="text-primary" />
            Built by engineers, for field service businesses
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: yImg }} className="relative">
            <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-primary/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-card shadow-2xl glow-green">
              <div className="flex items-center gap-1.5 border-b border-white/10 bg-black/50 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-primary/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                <span className="ml-2 text-xs text-muted-foreground">FlowCore — Operations Dashboard</span>
              </div>
              <div className="aspect-[4/3] w-full"><OpsDashboard /></div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="absolute -bottom-5 -left-5 rounded-2xl border border-white/10 bg-background/90 px-4 py-3 backdrop-blur-xl"
            >
              <p className="text-xs text-muted-foreground">Jobs completed today</p>
              <p className="font-heading text-2xl font-bold text-white">248 <span className="text-sm text-primary">▲ 12%</span></p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function LeanBand() {
  return (
    <section className="relative py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <Eyebrow className="mx-auto">Run leaner</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Stop losing thousands in wasted time, missed sales and lost stock
            </h2>
            <p className="mt-4 text-muted-foreground md:text-lg">
              FlowCore tightens every part of your operation — so your team does more, wastes less and never lets revenue slip through the cracks.
            </p>
          </div>
        </Reveal>

        <Stagger className="mt-14 grid grid-cols-2 gap-6 lg:grid-cols-4">
          {leanStats.map((s) => (
            <StaggerItem key={s.label}>
              <div className="rounded-2xl border border-white/10 bg-card p-7 text-center transition-colors hover:border-primary/40">
                <p className="font-heading text-4xl font-extrabold text-primary sm:text-5xl">
                  <Counter value={s.value} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                </p>
                <p className="mt-3 text-sm text-muted-foreground">{s.label}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function WhyFlowCore() {
  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Why FlowCore?"
            title="A generic CRM was never built for the field"
            sub="FlowCore replaces a patchwork of disconnected tools with one platform designed around how field service businesses actually operate."
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-white/10 bg-card/60 p-8">
              <h3 className="font-heading text-xl font-semibold text-muted-foreground">{comparison.generic.label}</h3>
              <ul className="mt-6 space-y-4">
                {comparison.generic.points.map((p) => (
                  <li key={p.text} className="flex items-start gap-3 text-muted-foreground">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white/5 text-white/40">
                      <XIcon size={14} weight="bold" />
                    </span>
                    <span className="line-through decoration-white/20">{p.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative h-full overflow-hidden rounded-3xl border border-primary/40 bg-card p-8 glow-green">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-semibold text-white">{comparison.flowcore.label}</h3>
                <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">Built for the field</span>
              </div>
              <ul className="mt-6 space-y-4">
                {comparison.flowcore.points.map((p) => (
                  <li key={p.text} className="flex items-start gap-3 text-white">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/20 text-primary">
                      <Check size={14} weight="bold" />
                    </span>
                    <span>{p.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ExplorePlatform() {
  const { openDemo } = useDemo();
  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Explore the platform"
            title="One platform. Seven powerful modules."
            sub="Every module works together seamlessly — no more juggling separate systems for sales, service, stock and safety."
          />
        </Reveal>

        <Stagger className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {platformModules.map((m, i) => {
            const Icon = m.icon;
            const wide = i === 0 ? "lg:col-span-1" : "";
            return (
              <StaggerItem key={m.name} className={wide}>
                <div className="group flex h-full flex-col rounded-2xl border border-white/10 bg-card p-7 transition-colors hover:border-primary/40 hover:bg-[#16181b]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-110">
                    <Icon size={26} weight="duotone" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-white">{m.name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                  <button
                    onClick={() => openDemo("demo")}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:gap-2"
                    data-testid={`module-${m.name.toLowerCase()}`}
                  >
                    Learn More <CaretRight size={14} weight="bold" />
                  </button>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

function KeyFeatures() {
  return (
    <section className="bg-card/30 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Key features"
            title="Everything you need to run operations"
            sub="Twelve tightly integrated capabilities that remove admin, reduce errors and keep engineers productive."
          />
        </Reveal>

        <Stagger className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" stagger={0.05}>
          {keyFeatures.map((f) => {
            const Icon = f.icon;
            return (
              <StaggerItem key={f.name}>
                <div className="group flex h-full items-start gap-4 rounded-2xl border border-white/10 bg-background p-6 transition-colors hover:border-primary/40">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-110">
                    <Icon size={22} weight="duotone" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-white">{f.name}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Built for your industry"
            title="Purpose-built for the trades that keep the world running"
            sub="From plumbers and electricians to gate installers, forklift maintenance and mobile engineers — FlowCore fits the way you work."
          />
        </Reveal>

        <Stagger className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" stagger={0.04}>
          {industries.map((ind) => {
            const Icon = ind.icon;
            return (
              <StaggerItem key={ind.name}>
                <div className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-card p-5 transition-all hover:-translate-y-1 hover:border-primary/40">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:rotate-6">
                    <Icon size={22} weight="duotone" />
                  </span>
                  <span className="text-sm font-medium text-white">{ind.name}</span>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

function Screenshots() {
  return (
    <section className="bg-card/30 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            align="center"
            eyebrow="See it in action"
            title="A platform your whole team will love"
            sub="From the operations desk to the engineer on site — FlowCore keeps everyone in sync."
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <BrowserFrame label="FlowCore — Operations Calendar" className="lg:col-span-2">
            <OpsCalendar />
          </BrowserFrame>
          <BrowserFrame label="FlowEngineer — Mobile App">
            <EngineerApp />
          </BrowserFrame>
          <BrowserFrame label="FlowCore — Live Jobs Board">
            <OpsDashboard />
          </BrowserFrame>
          <BrowserFrame label="FlowCore — Operations Dashboard" className="lg:col-span-2">
            <OpsDashboard />
          </BrowserFrame>
        </div>
      </div>
    </section>
  );
}

function PricingPreview() {
  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            align="center"
            eyebrow="Simple pricing"
            title="Plans that scale with your team"
            sub="Straightforward per-user pricing. No hidden fees. Cancel the noise, not the momentum."
          />
        </Reveal>

        <Stagger className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {pricingTiers.map((t) => (
            <StaggerItem key={t.name}>
              <div className={`relative flex h-full flex-col rounded-3xl border p-8 ${t.popular ? "border-primary/50 bg-card glow-green" : "border-white/10 bg-card/60"}`}>
                {t.popular && (
                  <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">Most Popular</span>
                )}
                <h3 className="font-heading text-lg font-semibold text-white">{t.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t.tagline}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-extrabold text-white">{t.price}</span>
                  <span className="text-sm text-muted-foreground">{t.period}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {t.features.slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check size={16} weight="bold" className="mt-0.5 flex-none text-primary" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-7">
                  <BookDemoButton
                    label={t.cta}
                    type={t.name === "Elite" ? "contact" : "demo"}
                    testid={`pricing-preview-${t.name.toLowerCase()}`}
                    className={`w-full justify-center ${t.popular ? "" : "bg-white/10 text-white hover:bg-white/15 hover:shadow-none"}`}
                  />
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <div className="mt-8 text-center">
          <Link to="/pricing" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:gap-2" data-testid="see-full-pricing">
            See full pricing & FAQs <CaretRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function OfferBand() {
  const perks = ["No commitment", "No payment required", "Full access to every feature"];
  return (
    <section className="relative px-5 py-12 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-primary/40 bg-primary/[0.06] p-8 sm:p-10 glow-green">
            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
            <div className="relative flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-primary-foreground">
                  <Gift size={14} weight="fill" /> Limited-time offer
                </span>
                <h2 className="mt-5 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
                  Try FlowCore free for <span className="text-primary">30 days</span>
                </h2>
                <p className="mt-4 text-base text-muted-foreground md:text-lg">
                  Get full access to the entire platform — no commitment and no payment required. Just results.
                </p>
                <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                  {perks.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm font-medium text-white">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/20 text-primary">
                        <CheckMini size={12} weight="bold" />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-none flex-col gap-3">
                <BookDemoButton label="Start Your Free Trial" testid="offer-start-trial" className="justify-center px-8 py-3.5 text-base" />
                <p className="text-center text-xs text-muted-foreground">No card needed · Cancel anytime</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BuiltByField() {
  const points = [
    { icon: HardHat, title: "Built by engineers", text: "Every workflow comes from people who've stood on site in the rain, not a boardroom." },
    { icon: UsersThree, title: "Designed by managers", text: "Shaped by operations managers who've run engineers, schedules and stock at scale." },
    { icon: WrenchIcon, title: "Proven in the real world", text: "Tested on real jobs, on the worst days — so it holds up when it actually matters." },
  ];
  return (
    <section className="relative overflow-hidden py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Built by the field, for the field</Eyebrow>
          <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
            Designed, built and run by real field service engineers and managers
          </h2>
          <p className="mt-5 max-w-xl leading-relaxed text-muted-foreground md:text-lg">
            FlowCore wasn't created by a software company chasing a trend. It's been built, designed and run by
            experienced field service engineers and managers — so every feature is grounded in how the job
            actually gets done, producing a system that works in the real world.
          </p>

          <div className="mt-8 space-y-4">
            {points.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-card p-5">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon size={22} weight="duotone" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-white">{p.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative">
            <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-primary/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10">
              <img src={ELEC_IMG} alt="Field service engineer at work" loading="lazy" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="absolute -bottom-6 left-6 right-10 rounded-2xl border border-white/10 bg-background/90 p-5 backdrop-blur-xl"
            >
              <Quotes size={22} weight="fill" className="text-primary" />
              <p className="mt-2 text-sm font-medium leading-relaxed text-white">
                "We built the platform we always wished we'd had on the tools — nothing bolted on, nothing that gets in the way."
              </p>
              <p className="mt-2 text-xs text-muted-foreground">— The FlowCore founding team</p>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden py-24">
      <div className="pointer-events-none absolute inset-0 radial-fade" />
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
            Ready to Transform Your Business?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-muted-foreground md:text-lg">
            Book a live demonstration and discover how FlowCore can simplify your operations, tighten your margins and free up your team.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <BookDemoButton testid="final-book-demo" />
            <LinkButton to="/contact" testid="final-contact">Contact Us</LinkButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "FlowCore",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description: "The complete operating platform for field service businesses — CRM, scheduling, engineers, servicing, assets, stock, customer portal and AI.",
    offers: { "@type": "Offer", price: "99", priceCurrency: "GBP" },
  };
  return (
    <>
      <SEO
        title="FlowCore — The Complete Operating Platform for Field Service"
        description="Run your entire field service business from one platform. FlowCore unifies CRM, scheduling, engineers, servicing, assets, stock, customer portals and AI. Book a demo today."
        jsonLd={jsonLd}
      />
      <KineticHero />
      <OfferBand />
      <BuiltByField />
      <LeanBand />
      <WhyFlowCore />
      <ExplorePlatform />
      <KeyFeatures />
      <Industries />
      <Screenshots />
      <PricingPreview />
      <FinalCTA />
    </>
  );
}
